import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { DashboardService } from '@ld.consumer-portal/data-access';
import { Loan } from '@ld.consumer-portal/models';
import { LoanSelectionService } from '@ld.consumer-portal/utils';
import { ApplicationTypeService } from './application-type.service';

export type PaperlessNavigationResult =
  | { type: 'no-loans' }
  | { type: 'single-loan'; loanNumber: string }
  | { type: 'multiple-loans'; loans: Loan[] };

@Injectable({
  providedIn: 'root',
})
export class PaperlessNavigationService {
  private readonly router = inject(Router);
  private readonly dashboardService = inject(DashboardService);
  private readonly loanSelectionService = inject(LoanSelectionService);
  private readonly applicationTypeService = inject(ApplicationTypeService);

  readonly showLoanSelectionModal = signal(false);

  readonly showNoLoansModal = signal(false);

  readonly showPaperlessIneligibleModal = signal(false);

  readonly ineligibleLoanProductType = signal<string | null>(null);

  readonly eligibleLoans = signal<Loan[]>([]);

  getPaperlessNavigationResult(): PaperlessNavigationResult {
    const loanResponses = this.dashboardService.loanResponses();

    const eligibleLoans = loanResponses.filter((response) => response.loan.paperlessInfo?.isEligible !== false);

    if (eligibleLoans.length === 0) {
      return { type: 'no-loans' };
    }

    if (eligibleLoans.length === 1) {
      return { type: 'single-loan', loanNumber: eligibleLoans[0].loan.loanNumber };
    }

    return { type: 'multiple-loans', loans: eligibleLoans.map((r) => r.loan) };
  }

  navigateToPaperlessSettings(): void {
    const selectedLoanNumber = this.loanSelectionService.selectedLoanNumber();
    if (selectedLoanNumber) {
      const loanResponses = this.dashboardService.loanResponses();
      const selectedResponse = loanResponses.find((r) => r.loan.loanNumber === selectedLoanNumber);
      if (selectedResponse?.loan.paperlessInfo?.isEligible === false) {
        const loanType = this.loanSelectionService.getLoanTypeShortName(selectedResponse.loan);
        this.ineligibleLoanProductType.set(loanType);
        this.showPaperlessIneligibleModal.set(true);
        return;
      }
    }

    const result = this.getPaperlessNavigationResult();

    switch (result.type) {
      case 'no-loans':
        this.showNoLoansModal.set(true);
        break;

      case 'single-loan':
        this.router.navigate(['/manage-paperless', result.loanNumber]);
        break;

      case 'multiple-loans': {
        const currentSelectedLoan = this.loanSelectionService.selectedLoanNumber();
        if (currentSelectedLoan) {
          const isEligible = result.loans.some((loan) => loan.loanNumber === currentSelectedLoan);
          if (isEligible) {
            this.router.navigate(['/manage-paperless', currentSelectedLoan]);
            return;
          }
        }
        this.eligibleLoans.set(result.loans);
        this.showLoanSelectionModal.set(true);
        break;
      }
    }
  }

  onLoanSelected(loanNumber: string): void {
    this.closeLoanSelectionModal();
    this.router.navigate(['/manage-paperless', loanNumber]);
  }

  closeLoanSelectionModal(): void {
    this.showLoanSelectionModal.set(false);
    this.eligibleLoans.set([]);
  }

  closeNoLoansModal(): void {
    this.showNoLoansModal.set(false);
  }

  closePaperlessIneligibleModal(): void {
    this.showPaperlessIneligibleModal.set(false);
    this.ineligibleLoanProductType.set(null);
  }

  onLinkLoan(): void {
    this.closeNoLoansModal();
    this.router.navigate(['/dashboard']);
  }

  onStartApplication(): void {
    this.closeNoLoansModal();
    this.applicationTypeService.openApplicationTypeSelector();
  }
}
